import { useCallback, useState } from 'preact/hooks';
import { ApiError, api, qs } from '../lib/api.js';
import { useFetch } from '../lib/useFetch.js';
import type { Invite, InvitesResponse, PlayersResponse, Role } from '../lib/types.js';
import { formatTs, relativeTime } from '../lib/format.js';

const ROLES: Role[] = ['player', 'founder', 'admin', 'owner'];

export function Players() {
  const [q, setQ] = useState('');
  const fetchPlayers = useCallback(
    () => api.get<PlayersResponse>(`/api/admin/players${qs({ q: q || undefined })}`),
    [q],
  );
  const players = useFetch(fetchPlayers, [q]);

  return (
    <div>
      <h1 class="page-title">Players</h1>

      <div class="card" style={{ marginBottom: '18px' }}>
        <input
          class="input"
          style={{ maxWidth: 320 }}
          placeholder="Search by name or email…"
          value={q}
          onInput={(e) => setQ((e.target as HTMLInputElement).value)}
        />
      </div>

      {players.error && <div class="error-banner">{players.error}</div>}
      <div class="card" style={{ marginBottom: '24px' }}>
        {(players.data?.players.length ?? 0) === 0 && !players.loading && (
          <div class="empty">No players found.</div>
        )}
        {(players.data?.players.length ?? 0) > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table class="grid">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Kind</th>
                  <th>Role</th>
                  <th>Wick</th>
                  <th>Lvl</th>
                  <th>Joined</th>
                  <th>Last seen</th>
                  <th>Banned</th>
                </tr>
              </thead>
              <tbody>
                {players.data!.players.map((p) => (
                  <PlayerRow key={p.id} player={p} onChanged={players.reload} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Invites />
    </div>
  );
}

function PlayerRow({
  player,
  onChanged,
}: {
  player: PlayersResponse['players'][number];
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setRole = async (role: string) => {
    setBusy(true);
    setError(null);
    try {
      await api.patch(`/api/admin/players/${player.id}`, { role });
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to update role.');
    } finally {
      setBusy(false);
    }
  };

  const toggleBan = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.patch(`/api/admin/players/${player.id}`, { banned: !player.banned });
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to update ban status.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <tr>
      <td>
        {player.name}
        {player.email && <div class="hint">{player.email}</div>}
        {error && (
          <div class="hint" style={{ color: 'var(--danger)' }}>
            {error}
          </div>
        )}
      </td>
      <td>{player.kind}</td>
      <td>
        <select
          class="select"
          style={{ width: 110 }}
          value={player.role}
          disabled={busy}
          onChange={(e) => setRole((e.target as HTMLSelectElement).value)}
        >
          {ROLES.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
      </td>
      <td>{player.wick}</td>
      <td>{player.level}</td>
      <td>{formatTs(player.createdAt)}</td>
      <td>{relativeTime(player.lastSeenAt)}</td>
      <td>
        <button
          class={`btn btn-sm${player.banned ? ' btn-primary' : ''}`}
          disabled={busy}
          onClick={toggleBan}
        >
          {player.banned ? 'Banned' : 'Ban'}
        </button>
      </td>
    </tr>
  );
}

function Invites() {
  const fetchInvites = useCallback(() => api.get<InvitesResponse>('/api/invites'), []);
  const invites = useFetch(fetchInvites, []);
  const [role, setRole] = useState<Role>('founder');
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [lastCode, setLastCode] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const generate = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ code: string }>('/api/invites', { role });
      setLastCode(res.code);
      invites.reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to create invite.');
    } finally {
      setBusy(false);
    }
  };

  const copy = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(code);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      // clipboard API may be unavailable — the code is still shown on screen
    }
  };

  const list: Invite[] = invites.data?.invites ?? [];

  return (
    <div class="card">
      <div class="section-label" style={{ marginTop: 0 }}>
        Invite codes
      </div>
      <div class="row">
        <select
          class="select"
          style={{ width: 140 }}
          value={role}
          onChange={(e) => setRole((e.target as HTMLSelectElement).value as Role)}
        >
          {ROLES.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
        <button class="btn btn-primary" disabled={busy} onClick={generate}>
          {busy ? 'Generating…' : 'Generate code'}
        </button>
        {lastCode && (
          <span class="chip" data-status="done">
            {lastCode}
            <button class="btn btn-sm" style={{ marginLeft: 8 }} onClick={() => copy(lastCode)}>
              {copied === lastCode ? 'Copied!' : 'Copy'}
            </button>
          </span>
        )}
      </div>
      {error && (
        <div class="error-banner" style={{ marginTop: 10 }}>
          {error}
        </div>
      )}

      {list.length > 0 && (
        <table class="grid" style={{ marginTop: 12 }}>
          <thead>
            <tr>
              <th>Code</th>
              <th>Role</th>
              <th>Used by</th>
              <th>Created</th>
            </tr>
          </thead>
          <tbody>
            {list.map((inv) => (
              <tr key={inv.code}>
                <td>
                  <code>{inv.code}</code>
                  <button
                    class="btn btn-sm"
                    style={{ marginLeft: 8 }}
                    onClick={() => copy(inv.code)}
                  >
                    {copied === inv.code ? 'Copied!' : 'Copy'}
                  </button>
                </td>
                <td>{inv.role}</td>
                <td>{inv.usedBy ? `#${inv.usedBy}` : '—'}</td>
                <td>{inv.createdAt ? formatTs(inv.createdAt) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
